import AbbrTip from '@/components/AbbrTip';

// AbbrText — Round 74 (Feb 11, 2026)
//
// Renders a string-prop narrative and AUTO-WRAPS any known stock-market
// abbreviation (FFO, AFFO, EPS, REIT, etc) with `<AbbrTip>` so users
// get inline definitions without the upstream string author having to
// know about JSX. Use this for narrative content that's BUILT in JS
// and FED to JSX as a string — e.g. Quality narrative bullets, Triangle
// trap-reason strings, REIT Cash Lens verdict narrative.
//
// Behavior:
//   * Each term in WHITELIST is tokenised on word boundaries so "EPS"
//     matches but "EPSILON" does not.
//   * Only the FIRST occurrence per term per AbbrText render is wrapped
//     — eliminates dotted-underline noise when a narrative says "EPS"
//     five times in one paragraph.
//   * Unmatched text falls straight through unchanged.
//   * Whitelist is sorted longest-first so "AFFO" matches before "FFO".
//
// Usage:
//   <AbbrText text="EPS lagged but ROIC stayed strong" />
//   <AbbrText text={trap_reason} className="text-sm text-foreground/80" as="p" />
//
// To add a new term:
//   1. Add to DEFINITIONS in AbbrTip.jsx (mandatory — that's where the
//      definition modal copy lives).
//   2. Add the term + its display rendering pattern (e.g. "P/E" vs
//      "FFO") to WHITELIST below.

// Longest first so multi-char terms like AFFO match before FFO.
// The display string is what AbbrText looks for in the input text
// (case-insensitive, word-boundary anchored). The `term` is the key
// passed to AbbrTip — must match a DEFINITIONS key in AbbrTip.jsx.
const WHITELIST = [
  ['Assets under management', 'AUM'],
  ['Debt-to-equity', 'DEBT_EQUITY'],
  ['Payout ratio', 'PAYOUT_RATIO'],
  ['EBITDA', 'EBITDA'],
  ['AFFO', 'AFFO'],
  ['CAGR', 'CAGR'],
  ['DRIP', 'DRIP'],
  ['GAAP', 'GAAP'],
  ['MAGI', 'MAGI'],
  ['NIIT', 'NIIT'],
  ['REIT', 'REIT'],
  ['ROIC', 'ROIC'],
  ['AUM',  'AUM'],
  ['BDC',  'BDC'],
  ['CEF',  'CEF'],
  ['D/E',  'DEBT_EQUITY'],
  ['EPS',  'EPS'],
  ['FCF',  'FCF'],
  ['FFO',  'FFO'],
  ['MLP',  'MLP'],
  ['NAV',  'NAV'],
  ['NOI',  'NOI'],
  ['ROA',  'ROA'],
  ['ROE',  'ROE'],
  ['TTM',  'TTM'],
  ['YoY',  'YOY'],
];

export default function AbbrText({ text, className = '', as: As = 'span' }) {
  if (!text || typeof text !== 'string') {
    return <As className={className}>{text || null}</As>;
  }

  // Each render starts with a clean "wrapped this term yet?" set so
  // first-occurrence-only is per-narrative, not per-page.
  const wrapped = new Set();
  const out = [];
  let remaining = text;
  let nodeKey = 0;

  // Iteratively chew through the string finding the earliest whitelist
  // hit. Word-boundary check uses lookaround-free logic that works
  // across all browsers (no `\b` issues with multibyte chars).
  while (remaining.length > 0) {
    let bestIdx = -1;
    let bestTerm = null;
    let bestDisplay = null;

    for (const [display, term] of WHITELIST) {
      if (wrapped.has(term)) continue;
      // Case-insensitive find from current position. We use the literal
      // term string in a manual scan so we can validate word boundaries
      // without RegExp Unicode quirks. Most stock terms are pure ASCII
      // so a simple-charcode check is safe + fast.
      const lower = remaining.toLowerCase();
      const needle = display.toLowerCase();
      let i = lower.indexOf(needle);
      while (i !== -1) {
        const before = i > 0 ? remaining.charCodeAt(i - 1) : 0;
        const after = i + needle.length < remaining.length
          ? remaining.charCodeAt(i + needle.length) : 0;
        const isAlnum = (c) => (c >= 48 && c <= 57)   // 0-9
          || (c >= 65 && c <= 90)   // A-Z
          || (c >= 97 && c <= 122); // a-z
        if (!isAlnum(before) && !isAlnum(after)) {
          // Valid word-boundary match. Pick the earliest hit across
          // the whitelist so terms don't get interleaved oddly.
          if (bestIdx === -1 || i < bestIdx) {
            bestIdx = i;
            bestTerm = term;
            // Preserve the original casing of the matched substring
            // (e.g. user wrote "YoY" — keep "YoY", not "YOY").
            bestDisplay = remaining.substring(i, i + needle.length);
          }
          break;
        }
        i = lower.indexOf(needle, i + 1);
      }
    }

    if (bestIdx === -1) {
      // No more matches — emit the rest verbatim.
      out.push(remaining);
      break;
    }

    // Emit the prefix unchanged, then the wrapped term.
    if (bestIdx > 0) out.push(remaining.substring(0, bestIdx));
    out.push(
      <AbbrTip key={`abbr-${nodeKey++}`} term={bestTerm}>
        {bestDisplay}
      </AbbrTip>
    );
    wrapped.add(bestTerm);
    remaining = remaining.substring(bestIdx + bestDisplay.length);
  }

  return <As className={className}>{out}</As>;
}
